import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark } from '../../Bookmark';
import { getCardTime } from '../../../utils/helpers/common';
import Checkbox from '../../Bulk/Checkbox';
import { EllipsisText, FlexBetween, FlexibleDiv } from '../../../styled/common';
import {
  BillOfSaleIcon,
  CardFooter,
  CardLink,
  CardParams,
  CardParamsLocation,
  CardParamsName,
  CardPreview,
  CardPreviewImage,
  FeaturedIcon,
  ItemCardStyled,
  InternalLoanPreviewLabel,
  Label,
  MortgageIcon,
  PriceContainer,
  VippedIcon,
  BulkItemLabel,
  BulkItemCheckMark
} from './styled';
import {
  getLocationName,
  getParamsList,
  joinTupleValues,
  withCacheBuster
} from './helpers';
import { BaseItemProps } from './types';
import { PriceDisplay } from './PriceDisplay';
import { profileItemStateLabels } from '../../../utils/constants/profileItemStatuses';
import { Breakpoints } from '../../../styled/constants';
import { useImageRetryOnReconnect } from '../../../hooks/useImageRetryOnReconnect';

const ItemCardComponent = <T extends BaseItemProps>(props: T) => {
  const { t, i18n } = useTranslation();

  const {
    stat,
    path,
    price,
    isPaidDaily,
    isLeased,
    isVipped,
    city,
    hasBillOfSale,
    hasMortgage,
    hasInternalLoan,
    isFeatured,
    id,
    inBookmarks,
    shouldUpdateCache = true,
    removeItemHandle,
    updatedAt,
    cypress = 'item-card-link',
    company,
    isBusiness,
    hideExcessItems = false,
    showBookmark = true,
    hasHorizontalScroll = false,
    bulkItem = false,
    handleClick,
    checkedItems,
    rooms,
    floor,
    area,
    floors,
    location,
    business,
    'cypress-card': card = 'item-card',
    button,
    deskCountItemsPerRows,
    hideLabels,
    priceView,
    state,
    showStateLabel = false,
    forceTargetBlank = false,
    priority = false,
    forceLargeImage = false,
    bookmarkStat = 'product-card-bookmark',
    preview
  } = props;

  const locationName = getLocationName({ city, location });
  const paramsList = getParamsList({ rooms, area, floor, floors }, t) as [
    string,
    string
  ][];
  const cardTime = getCardTime(updatedAt, i18n);

  const other = useMemo(() => joinTupleValues(paramsList), [paramsList]);

  const imgAlt = useMemo(
    () =>
      t('cards.item_card.img_alt', {
        id,
        city: city?.name,
        location: locationName,
        other,
        interpolation: { escapeValue: false }
      }),
    [id, city?.name, locationName, other]
  );

  const initialImgSrc = preview?.f460x345 || preview?.thumbnail;

  const { imgElementRef, retryCacheBuster } = useImageRetryOnReconnect();

  const initialSourceSrcSet = forceLargeImage
    ? preview?.f460x345
    : preview?.thumbnail;

  const thumbnail = preview ? (
    <picture key={retryCacheBuster}>
      <source
        media={`(max-width: ${Breakpoints.tablet}px)`}
        srcSet={withCacheBuster(initialSourceSrcSet, retryCacheBuster)}
      />
      <CardPreviewImage
        ref={imgElementRef}
        src={withCacheBuster(initialImgSrc, retryCacheBuster)}
        alt={imgAlt}
        width={460}
        height={345}
        {...(priority ? { fetchpriority: 'high' } : { loading: 'lazy' })}
      />
    </picture>
  ) : null;

  const itemType = company?.targetType?.toLowerCase() || '';

  const handleCardClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey) return;

    const isDesktop = window.matchMedia(
      `(min-width: ${Breakpoints.smallDesktop}px)`
    ).matches;

    if (isDesktop || forceTargetBlank) {
      e.preventDefault();
      window.open(path, '_blank');
    }
  };

  const cardLinkProps = {
    onClick: handleCardClick
  };

  return (
    <ItemCardStyled
      data-cy={card}
      $hideExcessItems={hideExcessItems}
      $hasHorizontalScroll={hasHorizontalScroll}
      $deskCountItemsPerRows={deskCountItemsPerRows}
      className="item-card"
      data-stat={stat}
    >
      <CardLink
        href={path!}
        cypress={cypress}
        $hasButton={!!button}
        aria-label={paramsList.join(',')}
        {...cardLinkProps}
      />
      {showBookmark && id && (
        <Bookmark
          itemId={id}
          isActive={inBookmarks}
          removeItemFunc={removeItemHandle}
          stat={bookmarkStat}
          shouldUpdateCache={shouldUpdateCache}
        />
      )}
      <CardPreview
        data-cy="item-card-preview"
        $bulkChecked={bulkItem && checkedItems?.includes(Number(id))}
      >
        <CardLink
          href={path!}
          data-cy="card-link"
          aria-label={`Photo ${paramsList}`}
          {...cardLinkProps}
        >
          {thumbnail}
        </CardLink>
        {hasInternalLoan && !hideLabels && (
          <InternalLoanPreviewLabel
            tag="caption_2"
            data-cy="item-card-internal-loan-label"
          >
            {t('cards.item_card.internal_loan')}
          </InternalLoanPreviewLabel>
        )}
        {!!itemType.length && (isBusiness || business?.id) && !hideLabels && (
          <Label
            tag="caption_2"
            $itemType={itemType}
            data-cy={`product-label-${itemType}`}
          >
            {t(`cards.item_card.${itemType}`)}
          </Label>
        )}
        {state && showStateLabel && (
          <Label
            tag="caption_1_new"
            $itemState={state}
            data-cy={`product-label-${state.toLowerCase()}`}
          >
            {t(profileItemStateLabels[state])}
          </Label>
        )}
      </CardPreview>
      <CardParams>
        <FlexBetween>
          <PriceContainer tag="h5_new" className="price-container">
            <PriceDisplay
              mode={priceView}
              price={{
                full: price?.total,
                currency: price?.currency,
                perAre: price?.perAre,
                units: area?.units
              }}
              isLeased={isLeased}
              isPaidDaily={isPaidDaily}
            />
          </PriceContainer>
          <FlexibleDiv>
            {hasBillOfSale && <BillOfSaleIcon />}
            {hasMortgage && <MortgageIcon />}
          </FlexibleDiv>
        </FlexBetween>
        <CardParamsLocation tag="body_1">{locationName}</CardParamsLocation>
        <CardParamsName tag="body_2">
          {(paramsList as [string, string][]).map(([key, value]) => (
            <span key={key}>{value}</span>
          ))}
        </CardParamsName>
        <CardFooter color="secondary" tag="caption_1">
          <FlexBetween>
            <EllipsisText suppressHydrationWarning data-cy="city_when">
              {city?.name}, {cardTime}
            </EllipsisText>

            {isFeatured && <FeaturedIcon />}
            {!isFeatured && isVipped && <VippedIcon />}
          </FlexBetween>

          {button}
        </CardFooter>
      </CardParams>
      {bulkItem && handleClick && (
        <BulkItemLabel htmlFor={String(id)}>
          {t('select')}
          <Checkbox
            key={id}
            type="checkbox"
            id={Number(id)}
            handleClick={handleClick}
            isChecked={checkedItems?.includes(Number(id))}
          />
          <BulkItemCheckMark />
        </BulkItemLabel>
      )}
    </ItemCardStyled>
  );
};

export const ItemCard = memo(ItemCardComponent);
